import { create } from 'zustand';
import type { ChatMessage, HealthStatus, VSTACKPlane } from '@/types/assistant';

export const DEFAULT_CEREBRO_CONTROL_BASE = import.meta.env.VITE_CONTROL_API_BASE || import.meta.env.VITE_API_BASE_URL || 'https://cerebrox.tfix.dev/api';
export const DEFAULT_CEREBRO_LOCAL_AUDIO_BASE = import.meta.env.VITE_LOCAL_AUDIO_API_BASE || 'http://127.0.0.1:9000';
// Backwards-compatible name for integrations that mean the durable control plane.
export const DEFAULT_CEREBRO_API_BASE = DEFAULT_CEREBRO_CONTROL_BASE;

interface AssistantState {
  messages: ChatMessage[];
  isLoading: boolean;
  health: HealthStatus | null;
  vstackPlanes: VSTACKPlane[];
  // apiBase is the durable Cloudflare control plane. Local audio execution is
  // deliberately separate so STT/TTS/DAW requests never fall into the edge.
  apiBase: string;
  localAudioBase: string;
  authToken: string;
  panelOpen: boolean;
  activeDock: string;

  setApiBase: (base: string) => void;
  setLocalAudioBase: (base: string) => void;
  setAuthToken: (token: string) => void;
  togglePanel: () => void;
  setPanelOpen: (open: boolean) => void;
  setActiveDock: (dock: string) => void;
  addMessage: (msg: ChatMessage) => void;
  updateMessage: (id: string, updates: Partial<ChatMessage>) => void;
  setLoading: (loading: boolean) => void;
  setHealth: (health: HealthStatus | null) => void;
  setVSTACKPlanes: (planes: VSTACKPlane[]) => void;
  clearMessages: () => void;
}

let messageCounter = 0;

export const useAssistantStore = create<AssistantState>((set) => ({
  messages: [
    {
      id: 'welcome',
      role: 'system',
      content: 'APE_XØRBIT Assistant online. Command intake ready.',
      timestamp: Date.now(),
      metadata: { type: 'system', status: 'success' }
    }
  ],
  isLoading: false,
  health: null,
  vstackPlanes: [],
  apiBase: DEFAULT_CEREBRO_CONTROL_BASE,
  localAudioBase: DEFAULT_CEREBRO_LOCAL_AUDIO_BASE,
  authToken: '',
  // The cinematic command atelier is the entry surface. Panels open only from
  // a deliberate scene object or dock action, never as a dashboard on arrival.
  panelOpen: false,
  activeDock: 'chat',

  setApiBase: (base) => set({ apiBase: base }),
  setLocalAudioBase: (base) => set({ localAudioBase: base }),
  setAuthToken: (token) => set({ authToken: token }),
  togglePanel: () => set((s) => ({ panelOpen: !s.panelOpen })),
  setPanelOpen: (open) => set({ panelOpen: open }),
  setActiveDock: (dock) => set({ activeDock: dock }),
  addMessage: (msg) => set((s) => ({ messages: [...s.messages, msg] })),
  updateMessage: (id, updates) =>
    set((s) => ({
      messages: s.messages.map((m) => (m.id === id ? { ...m, ...updates } : m))
    })),
  setLoading: (loading) => set({ isLoading: loading }),
  setHealth: (health) => set({ health }),
  setVSTACKPlanes: (planes) => set({ vstackPlanes: planes }),
  clearMessages: () =>
    set({
      messages: [
        {
          id: 'cleared',
          role: 'system',
          content: 'Conversation cleared. Awaiting new commands.',
          timestamp: Date.now(),
          metadata: { type: 'system', status: 'success' }
        }
      ]
    })
}));

export const generateMessageId = () => {
  messageCounter++;
  return `msg-${Date.now()}-${messageCounter}`;
};
